/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import { PhoneInput } from '@efex-components/phone/PhoneInput';
import { Alert, Button } from '@efexteam/components';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UserType } from '../../../../configs/language';
import clientSvc from '../../../../services/client/clientSvc';
import { aesDecryptCBC } from '../../../../utils/crypt';
import { capitalizeFirst } from '../../../../utils/formater';
import { selectProfileData } from '../../../profile/store/selectors';
import { useFormData } from '../context';
import { CreateContactPayload } from '../types';

interface IBaseData {
  formStep: number;
  loading?: boolean;
  nextFormStep: () => void;
  prevStep: () => void;
  createExisting: (data: CreateFromExistUser) => void;
}

interface CreateFromExistUser {
  uuid: string;
  email: string;
  name: string;
}

type FormData = {
  entity: string;
  email: string;
  firstName: string;
  lastName: string;
  contactPhone: string;
};

const BaseData = ({
  formStep,
  loading = false,
  nextFormStep,
  prevStep,
  createExisting,
}: IBaseData) => {
  const { t } = useTranslation();
  const { setFormValues, data } = useFormData();
  const [emailExists, setEmailExists] = useState<
    CreateContactPayload | undefined
  >(undefined);

  const userProfile = useSelector(selectProfileData);
  const userEmail = aesDecryptCBC(userProfile?.naturalPerson?.email || '');
  const enterpriseId = useMemo(() => {
    if (userProfile?.company) return userProfile?.company?.enterpriseId;
    return null;
  }, [userProfile]);

  const dropdownOptionsEntity: { key: string; value: UserType }[] = [
    { key: t('isNewUserCompany'), value: 'Company' },
    { key: t('isNewUserPerson'), value: 'Person' },
  ];

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    register,
    control,
  } = useForm<FormData>({ mode: 'all' });

  const onSubmit = handleSubmit((values) => {
    setFormValues(values);
    nextFormStep();
  });

  const handleBack = () => {
    prevStep();
  };

  const watchEntity = watch(['entity']);

  const validateEmail = async (search: string) => {
    setEmailExists(undefined);
    const { results } = await clientSvc.searchContactsV2ByEmail({
      enterpriseId: enterpriseId || '',
      search,
    });
    if (results.length > 0) {
      setEmailExists(results[0]);
    } else {
      setEmailExists(undefined);
    }
  };

  const shouldPerformSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/)) {
      validateEmail(value);
    }
  };

  useEffect(() => {
    if (emailExists) {
      const { contactNaturalPersonEnterprise } = emailExists || {};
      const { naturalPerson, enterprise } = contactNaturalPersonEnterprise;
      const { entity_type: entityType } = enterprise || {};
      const { firstName, lastName, phoneNumber } = naturalPerson;
      if (entityType) {
        setValue(
          'entity',
          dropdownOptionsEntity.find(
            (option) => option.value === capitalizeFirst(entityType)
          )?.value || dropdownOptionsEntity[0].value,
          {
            shouldValidate: true,
          }
        );
      }
      if (phoneNumber) {
        setValue('contactPhone', phoneNumber);
      }
      if (firstName) {
        setValue('firstName', firstName);
      }
      if (lastName) {
        setValue('lastName', lastName);
      }
    } else {
      setValue('firstName', '');
      setValue('lastName', '');
      setValue('entity', dropdownOptionsEntity[0].value);
      setValue('contactPhone', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailExists]);

  useEffect(() => {
    if (data && !emailExists) {
      if (data.email) {
        setValue('email', data.email);
      }
      if (data.firstName) {
        setValue('firstName', data.firstName);
      }
      if (data.lastName) {
        setValue('lastName', data.lastName);
      }
      if (data.contactPhone) {
        setValue('contactPhone', data.contactPhone);
      }
      if (data.entity) {
        setValue(
          'entity',
          dropdownOptionsEntity.find((option) => option.value === data.entity)
            ?.value || dropdownOptionsEntity[0].value,
          {
            shouldValidate: true,
          }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, emailExists]);

  return (
    <>
      <Typography variant="h6" className="!my-4 text-center">
        {t('ccProviderTitle')}
      </Typography>

      {emailExists ? (
        <Alert variant="outlined" className="my-5" severity="info">
          {t('ccEmailExistMessage')}
        </Alert>
      ) : (
        <Alert variant="outlined" severity="info" className="!my-[15px]">
          {t('ccCompanyDocumentationWarning')}
        </Alert>
      )}

      <form onSubmit={onSubmit} autoComplete="off">
        <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="entity">{t('entity')}</InputLabel>
              <Controller
                name="entity"
                control={control}
                defaultValue={dropdownOptionsEntity[0].value}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    labelId="entity"
                    id="entity"
                    defaultValue={dropdownOptionsEntity[0].value}
                    label={t('entity')}
                    value={value}
                    onChange={onChange}
                    disabled={!!emailExists}
                  >
                    {dropdownOptionsEntity.map((entity) => (
                      <MenuItem key={entity.key} value={entity.value}>
                        {entity.key}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.entity && (
                <p className="text-orange-600 text-xs">{t('defaultError')}</p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                id="email"
                type="email"
                label={t('email')}
                InputLabelProps={{ shrink: true }}
                error={!!errors.email}
                {...register('email', {
                  required: true,
                  validate: (value, _) => value !== userEmail,
                })}
                onChange={shouldPerformSearch}
              />
              {errors.email && errors.email.type === 'required' && (
                <p className="text-orange-600 text-xs">{t('defaultError')}</p>
              )}
              {errors.email && errors.email.type === 'validate' && (
                <>
                  <p className="text-orange-600 text-xs">
                    {t('ccErrorSameEmail1')}
                  </p>
                  <p className="text-orange-600 text-xs">
                    {t('ccErrorSameEmail2')}
                  </p>
                </>
              )}
            </FormControl>
          </Grid>
          {watchEntity[0] === 'Person' ? (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    id="firstName"
                    label={
                      watchEntity[0] === dropdownOptionsEntity[0].value
                        ? t('firstName')
                        : t('ccName')
                    }
                    error={!!errors.firstName}
                    InputLabelProps={{ shrink: true }}
                    disabled={!!emailExists}
                    {...register('firstName', {
                      required: true,
                      pattern: {
                        value: /^[a-zA-Z0-9. ]*$/i,
                        message: '',
                      },
                    })}
                  />
                  {errors.firstName && errors.firstName.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.firstName && errors.firstName.type === 'pattern' && (
                    <p className="text-orange-600 text-xs">
                      {t('ccErrorNoSpecialCharacters')}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    id="lastName"
                    label={t('ccFullLastName')}
                    error={!!errors.lastName}
                    InputLabelProps={{ shrink: true }}
                    disabled={!!emailExists}
                    {...register('lastName', {
                      required: true,
                      pattern: {
                        value: /^[a-zA-Z0-9. ]*$/i,
                        message: '',
                      },
                    })}
                  />
                  {errors.lastName && errors.lastName.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.lastName && errors.lastName.type === 'pattern' && (
                    <p className="text-orange-600 text-xs">
                      {t('ccErrorNoSpecialCharacters')}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} className="!pt-2">
                <Alert severity="warning" className="w-full !mb-5 !mt-1">
                  {t('ccPersonNameHelper')}
                </Alert>
              </Grid>
            </>
          ) : null}
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth>
              <Controller
                name="contactPhone"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    className="w-full"
                    label={`${t('ccContactPhone')}`}
                    value={value}
                    onChange={onChange}
                    error={!!errors.contactPhone}
                  />
                )}
              />
              {errors.contactPhone &&
                errors.contactPhone.type === 'required' && (
                  <p className="text-orange-600 text-xs">{t('defaultError')}</p>
                )}
            </FormControl>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          className="flex justify-between !flex-col md:!flex-row !mt-4"
        >
          {!emailExists && (
            <Button
              appearance="primary"
              size="large"
              className="mt-4"
              type="submit"
            >
              {t('continue')}
            </Button>
          )}
        </Grid>
      </form>
    </>
  );
};

export default BaseData;
